import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Tab, MenuItem, Select, FormControl, InputLabel, IconButton
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import * as XLSX from 'xlsx';
import { API_URL } from '../../config';
import WhatsAppSendButton from '../common/WhatsAppSendButton';

const GREEN  = '#1b4332';
const ORANGE = '#f59e0b';

const HDR_SX = {
  fontWeight: 700, color: '#fff', fontSize: '0.75rem', textTransform: 'uppercase',
  background: `linear-gradient(135deg, ${ORANGE} 0%, #ffa726 100%)`,
  borderRight: '1px solid rgba(255,255,255,0.2)', py: 1.2, px: 0.8,
  whiteSpace: 'nowrap',
};
const CELL_SX = { py: 1, px: 0.8, fontSize: '0.78rem' };

function fmtNum(v, d = 0) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}

function isGrandTotal(row) {
  return String(row.HPNAME || row.COMPANY || '').trim().toUpperCase().startsWith('GRAND TOTAL');
}

function isTotalRow(row) {
  return String(row.COMPANY || '').trim().toUpperCase() === 'TOTAL';
}

// ─── Detail table (RS 0 / RS 2) ──────────────────────────────────────────────
function DetailTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  const headers = ['HP NAME','LOT NO','THICK','LENGTH','WIDTH','PCS','GLUE TYPE','NO OF GLUE LINE','NO OF DRY LINE','NO OF FACE LINE','FACE CBM','GLUE LINE','CBM','MARKET'];
  return (
    <TableContainer sx={{ maxHeight: 560, overflowX: 'auto' }}>
      <Table stickyHeader size="small" sx={{ minWidth: 1100 }}>
        <TableHead>
          <TableRow>{headers.map(h => <TableCell key={h} sx={{ ...HDR_SX, textAlign: 'center' }}>{h}</TableCell>)}</TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{
              backgroundColor: i % 2 === 0 ? '#fff' : '#f8faf9',
              '&:hover': { backgroundColor: '#e8f5e9' },
            }}>
              <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: 600, color: GREEN }}>{row.HPNAME || '—'}</TableCell>
              <TableCell sx={{ ...CELL_SX, textAlign: 'center' }}>{row.LOT_NUMBER || '—'}</TableCell>
              <TableCell sx={{ ...CELL_SX, textAlign: 'center' }}>{fmtNum(row.THICKNESS, 2)}</TableCell>
              <TableCell sx={{ ...CELL_SX, textAlign: 'center' }}>{fmtNum(row.LENGTH, 3)}</TableCell>
              <TableCell sx={{ ...CELL_SX, textAlign: 'center' }}>{fmtNum(row.WIDTH, 3)}</TableCell>
              <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: 600, color: '#2e7d32' }}>{fmtNum(row.PCS, 0)}</TableCell>
              <TableCell sx={{ ...CELL_SX, textAlign: 'center' }}>{row.GLUETYPE || '—'}</TableCell>
              <TableCell sx={{ ...CELL_SX, textAlign: 'center' }}>{fmtNum(row.NO_OF_GLUELINE, 0)}</TableCell>
              <TableCell sx={{ ...CELL_SX, textAlign: 'center' }}>{fmtNum(row.NO_OF_DRYLINE, 0)}</TableCell>
              <TableCell sx={{ ...CELL_SX, textAlign: 'center' }}>{fmtNum(row.NO_OF_FACE_LINE, 0)}</TableCell>
              <TableCell sx={{ ...CELL_SX, textAlign: 'center' }}>{fmtNum(row.CBM_FACE, 5)}</TableCell>
              <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: 600 }}>{fmtNum(row.GLUE_LINE, 2)}</TableCell>
              <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: 600, color: GREEN }}>{fmtNum(row.CBM, 3)}</TableCell>
              <TableCell sx={{ ...CELL_SX, textAlign: 'center' }}>{row.MARKETNAME || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Totals table (RS 1 / RS 3) ───────────────────────────────────────────────
function TotalsTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 600 }}>
        <TableHead>
          <TableRow>
            {['HP NAME','PCS','FACE CBM','GLUE LINE','CBM'].map(h => (
              <TableCell key={h} sx={{ ...HDR_SX, textAlign: 'center' }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const grand = isGrandTotal(row);
            return (
              <TableRow key={i} sx={{ backgroundColor: grand ? '#fef3c7' : (i % 2 === 0 ? '#fff' : '#f8faf9') }}>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : GREEN }}>{row.HPNAME || '—'}</TableCell>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : '#2e7d32' }}>{fmtNum(row.PCS, 0)}</TableCell>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', color: grand ? '#92400e' : '#555' }}>{fmtNum(row.CBM_FACE, 5)}</TableCell>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : '#555' }}>{fmtNum(row.GLUE_LINE, 2)}</TableCell>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: grand ? 700 : 600, color: grand ? '#92400e' : GREEN }}>{fmtNum(row.CBM, 3)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Company summary (RS 4) ───────────────────────────────────────────────────
function SummaryTable({ rows }) {
  if (!rows || rows.length === 0) return <Alert severity="info" sx={{ m: 2 }}>No data</Alert>;
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 400 }}>
        <TableHead>
          <TableRow>
            {['COMPANY','PCS','CBM'].map(h => (
              <TableCell key={h} sx={{ ...HDR_SX, textAlign: 'center' }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const total = isTotalRow(row);
            return (
              <TableRow key={i} sx={{ backgroundColor: total ? '#fef3c7' : (i % 2 === 0 ? '#fff' : '#f8faf9') }}>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: total ? 700 : 600, color: total ? '#92400e' : GREEN }}>{row.COMPANY || '—'}</TableCell>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: total ? 700 : 600, color: total ? '#92400e' : '#2e7d32' }}>{fmtNum(row.PCS, 0)}</TableCell>
                <TableCell sx={{ ...CELL_SX, textAlign: 'center', fontWeight: total ? 700 : 600, color: total ? '#92400e' : GREEN }}>{fmtNum(row.CBM, 3)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const TAB_LABELS = [
  'Star-1 Detail', 'Star-1 Totals',
  'Star-2 Detail', 'Star-2 Totals',
  'Company Summary',
];

const DailyHotpressReportV2View = ({ onBackClick, user }) => {
  const [date, setDate]       = useState(null);
  const [shift, setShift]     = useState('DAY');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [data, setData]       = useState(null);
  const [tab, setTab]         = useState(0);

  const fetchData = async () => {
    if (!date) { setError('Please select a date'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const res = await fetch(`${API_URL}/order/dailyhotpressreportv2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: date.format('YYYY-MM-DD'), shift }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      if (!apiData.data || apiData.data.every(rs => rs.length === 0)) {
        setError('No data found for the selected date and shift.');
        return;
      }
      setData(apiData.data);
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    TAB_LABELS.forEach((label, i) => {
      if (data[i]?.length) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data[i]), label.substring(0, 31));
      }
    });
    XLSX.writeFile(wb, `Hotpress_Report_${shift}_${date.format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(27,67,50,0.1)', border: '1px solid #e8f5e9' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: GREEN }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{ background: `linear-gradient(135deg, ${GREEN} 0%, #2d6a4f 100%)`, borderRadius: 2, p: 1.5, mr: 2 }}>
              <LocalFireDepartmentIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: GREEN }}>Daily Hotpress Production Report</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>Lot-level detail by date and shift — STAR-1 & STAR-2</Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
              <DatePicker label="Date" value={date} onChange={setDate}
                slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }} />
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel>Shift</InputLabel>
                <Select value={shift} label="Shift" onChange={e => setShift(e.target.value)}>
                  <MenuItem value="DAY">DAY</MenuItem>
                  <MenuItem value="NIGHT">NIGHT</MenuItem>
                </Select>
              </FormControl>
              <Button variant="contained" size="large" startIcon={<SearchIcon />} onClick={fetchData}
                disabled={loading}
                sx={{ minWidth: 140, backgroundColor: GREEN, '&:hover': { backgroundColor: '#0f2419' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                Search
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {loading && <Box textAlign="center" py={6}><CircularProgress sx={{ color: GREEN }} /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #e8f5e9' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1 }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="inherit" variant="scrollable" scrollButtons="auto"
              sx={{ '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem' }, '& .Mui-selected': { color: GREEN }, '& .MuiTabs-indicator': { backgroundColor: GREEN } }}>
              {TAB_LABELS.map((l, i) => <Tab key={i} label={l} />)}
            </Tabs>
            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}
              sx={{ color: GREEN, fontWeight: 600, textTransform: 'none', ml: 1, flexShrink: 0 }}>Excel</Button>
          </Box>
          {tab === 0 && <DetailTable rows={data[0]} />}
          {tab === 1 && <TotalsTable rows={data[1]} />}
          {tab === 2 && <DetailTable rows={data[2]} />}
          {tab === 3 && <TotalsTable rows={data[3]} />}
          {tab === 4 && <SummaryTable rows={data[4]} />}
        </Card>
      )}

      {data && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          <WhatsAppSendButton
            user={user}
            label={`Send Hotpress ${shift} Report to WhatsApp`}
            apiUrl={date ? `${API_URL}/api/hotpress-v2/test-${shift.toLowerCase()}?date=${date.format('YYYY-MM-DD')}` : null}
            disabled={!date}
          />
        </Box>
      )}
    </Container>
  );
};

export default DailyHotpressReportV2View;
